import {useCallback, useMemo} from 'react';

import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {AnalyticsDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/analyticsDataEventObject';
import {
  InterfaceEventsEnum,
  trackDataEvent,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  AnalyticsPanelIdSelector,
  getDefaultAnalyticsPanelId,
} from '../../../store/panels/analytics/analyticsPanelSelectors';
import {AnalyticsSelectedViewId} from '../../../store/panels/analytics/views/analyticsPanelViewsState';
import {makeGetAnalyticsTrackingData, serializeSavedViewId} from './analyticsSelectors';

/**
 * @deprecated use trackAnalyticsFrontendEvent instead ** for new events only **.
 */
export function useAnalyticsTracking(
  getPanelId: AnalyticsPanelIdSelector<void> = getDefaultAnalyticsPanelId,
) {
  const getAnalyticsTrackingData = useMemo(() => makeGetAnalyticsTrackingData(getPanelId), [getPanelId]);
  const trackingData = useAppSelector(getAnalyticsTrackingData);

  return (type: InterfaceEventsEnum, eventData?: {[key: string]: any}) => {
    const {saved_view_id: savedViewId, ...otherEventData} = eventData ?? {};
    trackInterfaceEvent(type, {
      ...trackingData,
      // If the user provided another saved_view_id, we serialize it & override the existing one.
      ...(savedViewId ? serializeSavedViewId(savedViewId) : {}),
      ...otherEventData,
    });
  };
}

type ObjectDataWithSavedViewId = Record<string, unknown> & {saved_view_id?: AnalyticsSelectedViewId};

export function useAnalyticsTrackingFrontendEvent(getPanelId: AnalyticsPanelIdSelector<void>) {
  const getAnalyticsTrackingData = useMemo(() => makeGetAnalyticsTrackingData(getPanelId), [getPanelId]);
  const trackingData = useAppSelector(getAnalyticsTrackingData);

  return useCallback(
    (
      action: DataEventActionsEnum,
      objectName: AnalyticsDataEventObject,
      objectData: ObjectDataWithSavedViewId = {},
    ) => {
      const {report_type: reportType, ...otherTrackingData} = trackingData;

      const {saved_view_id: savedViewId, ...otherEventData} = objectData ?? {};

      // If the user provided another saved_view_id, we serialize it & override the existing one.
      const serializedSavedView = savedViewId ? serializeSavedViewId(savedViewId) : {};

      const payload = {
        ...otherTrackingData,
        ...objectData,
        ...serializedSavedView,
        ...otherEventData,
      };

      // TODO(ldemarne): Use `DataEventTrackingContext`.
      trackDataEvent({name: 'analytics', payload: {report_type: reportType}}, action, objectName, payload);
    },
    [trackingData],
  );
}
